import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';

import { usePluralization } from 'common/hooks';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { useAddOptionsTracking } from 'modules/menu/hooks/telemetryHooks';
import { OptionsIcon } from 'modules/menu/items/components/OptionBinding';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { OfferWithSelectionAndAuthor, OfferingApiError } from 'modules/shared-menu/types';

import ActionButton from './ActionButton';
import BulkActionErrorList from './BulkActionErrorList';
import { BulkActionModalTitle } from './BulkActionModalTitle';
import { buildUpdateOffersPayload } from './transformers';
import { useBatchUpdateOffers } from './useBatchUpdateOffers';
import { updateLocalSelectionData } from './utils';
import useBulkSelection from '../../../hooks/useBulkSelection';
import { OptionBinding } from '../../../types';
import { OptionBindingsModal } from '../../OptionBindings/AddOptionsToItem/OptionBindingsModal';
import { buildOptionBindingCreatePayload } from '../../OptionBindings/utils';

type BulkAddOptionBindingProps = {
  selectedIds: Set<string>;
};

export const BulkAddOptionBinding = ({ selectedIds }: BulkAddOptionBindingProps) => {
  const intl = useIntl();
  const { formatPluralMessage } = usePluralization();
  const [isBulkAddOptionBindingModalOpen, setIsBulkAddOptionBindingModalOpen] = useState(false);
  const { data: user } = useCurrentUser();
  const { offers, selection, options, updateSelectionData } = useSelectionData();

  const { selectedIds: selectedOptionIds, select: selectOption, deselect: deselectOption } = useBulkSelection();
  const { executeBatchUpdateOffers, isLoading } = useBatchUpdateOffers();

  const trackAddOptions = useAddOptionsTracking([...selectedIds], [...selectedOptionIds]);

  const onSuccess = useCallback(
    (successfullyUpdatedOffers: OfferWithSelectionAndAuthor[]) => {
      toast.success(
        formatPluralMessage({
          id: 'shared-menu.bulk-actions.toasts.update.success-description',
          count: successfullyUpdatedOffers.length,
        }),
      );
      updateLocalSelectionData(successfullyUpdatedOffers, updateSelectionData);
    },
    [formatPluralMessage, updateSelectionData],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          allOffers={offers}
          errors={errors}
          selectionPrimaryLanguage={selection.primary_language}
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.update.error-description' })}
        />,
        { duration: Infinity },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const selectedOffers = useMemo(() => offers.filter(offer => selectedIds.has(offer.id)), [offers, selectedIds]);

  const isSomeWeightBasedItemSelected = useMemo(
    () => selectedOffers.some(offer => !!offer.sell_by_weight_config),
    [selectedOffers],
  );

  const disableBulkAddOptions = isSomeWeightBasedItemSelected;

  const optionBindingsToCreate = useMemo<OptionBinding[]>(() => {
    const selectedOptions = options.filter(option => selectedOptionIds.has(option.id));
    return selectedOptions.map(option => ({ id: '', ...buildOptionBindingCreatePayload(option) }));
  }, [options, selectedOptionIds]);

  const handleConfirm = useCallback(async () => {
    const offersWithOptionBindingsToCreate = selectedOffers.map(offer => {
      return {
        ...offer,
        option_bindings: [...(offer?.option_bindings ?? []), ...optionBindingsToCreate],
      };
    });

    const offerPayloads = buildUpdateOffersPayload(
      offersWithOptionBindingsToCreate,
      selection.id,
      user?._id.$oid ?? '',
    );

    await executeBatchUpdateOffers({
      offerPayloads,
      onSuccess,
      onError,
    });

    trackAddOptions();
    setIsBulkAddOptionBindingModalOpen(false);
  }, [
    executeBatchUpdateOffers,
    onError,
    onSuccess,
    optionBindingsToCreate,
    selectedOffers,
    selection.id,
    trackAddOptions,
    user?._id.$oid,
  ]);

  return (
    <>
      <ActionButton
        disabled={disableBulkAddOptions}
        icon={<OptionsIcon />}
        onClick={() => setIsBulkAddOptionBindingModalOpen(true)}
        tooltipTitle={
          <FormattedMessage
            id={
              disableBulkAddOptions
                ? 'menu.bulkActions.addOptionsToItems.disabledLabel'
                : 'menu.bulkActions.addOptionsToItems.addOptions'
            }
          />
        }
      />
      {isBulkAddOptionBindingModalOpen && (
        <OptionBindingsModal
          loading={isLoading}
          mode="bulkAction"
          onClose={() => setIsBulkAddOptionBindingModalOpen(false)}
          onDeselectOption={deselectOption}
          isOpen
          onSelectOption={selectOption}
          onDone={handleConfirm}
          selectedOptionIds={selectedOptionIds}
          title={
            <BulkActionModalTitle
              count={selectedIds.size}
              title={<FormattedMessage id="menu.bulkActions.addOptionsToItems.addOptions" />}
            />
          }
        />
      )}
    </>
  );
};
